/**
 * Type-0x4A (Seismic) envelope encoding — read path only.
 *
 * Ported verbatim from `packaged/safe-app-src20/src/seismic/envelope.ts`.
 * We use only the messageVersion-2 (EIP-712) path because MetaMask can sign
 * that; the messageVersion-0 (raw sighash) relayer path is intentionally
 * absent in this port — spec §6.2/§6.3.1 explicitly drops the relayer key.
 */
import { keccak256, toRlp, type Hex } from 'viem'

export const TX_TYPE_SEISMIC = 0x4a
export const MESSAGE_VERSION_EIP712 = 2

/**
 * Post-hardfork `TxSeismic` EIP-712 field, added after `signedRead`. It is
 * `keccak256(RLP(authorization_list))`, mirroring seismic-viem's
 * `encodeAuthorizationList()` (dist/_esm/index.js). This read path never
 * sends an authorization list, so it's always the empty-list hash.
 */
export const EMPTY_AUTHORIZATION_LIST_HASH: Hex = keccak256(toRlp([]))

/**
 * Alloy/Ethereum convention: integers encoded as minimal big-endian byte
 * strings; 0 → empty byte string. NEVER `0x00` for zero — RLP treats them as
 * different encodings and the hash diverges.
 */
export function rlpInt(n: bigint | number): Hex {
  const b = typeof n === 'bigint' ? n : BigInt(n)
  if (b === 0n) return '0x'
  let hex = b.toString(16)
  if (hex.length % 2 !== 0) hex = '0' + hex
  return `0x${hex}`
}

/** 20-byte address. 0x0..0 → empty byte string (TxKind::Create); else 20 bytes. */
export function rlpAddr(addr: Hex | null | undefined): Hex {
  if (!addr) return '0x'
  const stripped = addr.toLowerCase().replace(/^0x/, '')
  if (/^0+$/.test(stripped)) return '0x'
  return `0x${stripped}` as Hex
}

/** RLP-encode a raw byte string. Pass an already-`0x`-prefixed hex value. */
export function rlpBytes(hex: Hex): Hex {
  return hex
}

export type EnvelopeFields = {
  chainId: number
  nonce: bigint
  gasPrice: bigint
  gasLimit: bigint
  to: Hex
  value: bigint
  /** 33-byte compressed secp256k1 client ephemeral pubkey. */
  encryptionPubkey: Hex
  /** U96 (12-byte big-endian, but RLP strips leading zeros). */
  encryptionNonce: bigint
  messageVersion: number
  recentBlockHash: Hex
  expiresAtBlock: bigint
  signedRead: boolean
  /** AES-256-GCM ciphertext || 16-byte tag, hex-prefixed. */
  ciphertext: Hex
}

/** RLP-encode the 14-field signed body (unsigned + y_parity + r + s). */
export function encodeSigned(f: EnvelopeFields, yParity: number, r: bigint, s: bigint): Hex {
  return toRlp([
    rlpInt(f.chainId),
    rlpInt(f.nonce),
    rlpInt(f.gasPrice),
    rlpInt(f.gasLimit),
    rlpAddr(f.to),
    rlpInt(f.value),
    rlpBytes(f.encryptionPubkey),
    rlpInt(f.encryptionNonce),
    rlpInt(f.messageVersion),
    rlpBytes(f.recentBlockHash),
    rlpInt(f.expiresAtBlock),
    f.signedRead ? '0x01' : '0x',
    rlpBytes(f.ciphertext),
    [], // empty authorization_list (RLP wire body still carries the raw list, not its hash — verified against seismic-viem's serializeSeismicTransaction)
    rlpInt(yParity),
    rlpInt(r),
    rlpInt(s),
  ])
}

/**
 * AAD = RLP(11 fields). Mirrors TxSeismicMetadata exactly. `sender` is the EOA
 * that will sign the envelope (NOT the encryption key).
 */
export function encodeAad(args: {
  sender: Hex
  chainId: number
  nonce: bigint
  to: Hex
  value: bigint
  encryptionPubkey: Hex
  encryptionNonce: bigint
  messageVersion: number
  recentBlockHash: Hex
  expiresAtBlock: bigint
  signedRead: boolean
}): Hex {
  return toRlp([
    rlpBytes(args.sender),
    rlpInt(args.chainId),
    rlpInt(args.nonce),
    rlpAddr(args.to),
    rlpInt(args.value),
    rlpBytes(args.encryptionPubkey),
    rlpInt(args.encryptionNonce),
    rlpInt(args.messageVersion),
    rlpBytes(args.recentBlockHash),
    rlpInt(args.expiresAtBlock),
    args.signedRead ? '0x01' : '0x',
  ])
}

/** Final wire form: 0x4A || RLP(signed_body), hex-prefixed. */
export function rawEnvelope(signedBody: Hex): Hex {
  return `0x4a${signedBody.slice(2)}`
}

export type TxSeismicTypedDataFields = {
  chainId: number
  nonce: bigint
  gasPrice: bigint
  gasLimit: bigint
  to: Hex
  value: bigint
  /** ENCRYPTED ciphertext (encryption happens before signing), hex-prefixed. */
  input: Hex
  /** 33-byte compressed secp256k1 client ephemeral pubkey, hex-prefixed. */
  encryptionPubkey: Hex
  encryptionNonce: bigint
  recentBlockHash: Hex
  expiresAtBlock: bigint
  signedRead: boolean
}

/**
 * Build the EIP-712 `TxSeismic` typed data (messageVersion 2) for
 * `eth_signTypedData_v4`. Field ORDER and Solidity TYPES must match Seismic
 * exactly — note the non-obvious widths and the extra `isCreate` bool that is
 * NOT present in the RLP wire body. The wallet hashes the dynamic `bytes`
 * fields itself, so pass raw values here.
 */
export function txSeismicTypedData(f: TxSeismicTypedDataFields) {
  const isCreate = /^0x0*$/.test(f.to)
  return {
    types: {
      EIP712Domain: [
        { name: 'name', type: 'string' },
        { name: 'version', type: 'string' },
        { name: 'chainId', type: 'uint256' },
        { name: 'verifyingContract', type: 'address' },
      ],
      TxSeismic: [
        { name: 'chainId', type: 'uint64' },
        { name: 'nonce', type: 'uint64' },
        { name: 'gasPrice', type: 'uint128' },
        { name: 'gasLimit', type: 'uint64' },
        { name: 'to', type: 'address' },
        { name: 'isCreate', type: 'bool' },
        { name: 'value', type: 'uint256' },
        { name: 'input', type: 'bytes' },
        { name: 'encryptionPubkey', type: 'bytes' },
        { name: 'encryptionNonce', type: 'uint96' },
        { name: 'messageVersion', type: 'uint8' },
        { name: 'recentBlockHash', type: 'bytes32' },
        { name: 'expiresAtBlock', type: 'uint64' },
        { name: 'signedRead', type: 'bool' },
        { name: 'authorizationListHash', type: 'bytes32' },
      ],
    },
    primaryType: 'TxSeismic' as const,
    domain: {
      name: 'Seismic Transaction',
      version: '2',
      chainId: f.chainId,
      verifyingContract: '0x0000000000000000000000000000000000000000',
    },
    message: {
      chainId: f.chainId,
      nonce: f.nonce.toString(),
      gasPrice: f.gasPrice.toString(),
      gasLimit: f.gasLimit.toString(),
      to: f.to,
      isCreate,
      value: f.value.toString(),
      input: f.input,
      encryptionPubkey: f.encryptionPubkey,
      encryptionNonce: f.encryptionNonce.toString(),
      messageVersion: MESSAGE_VERSION_EIP712,
      recentBlockHash: f.recentBlockHash,
      expiresAtBlock: f.expiresAtBlock.toString(),
      signedRead: f.signedRead,
      authorizationListHash: EMPTY_AUTHORIZATION_LIST_HASH,
    },
  }
}

/** Split a 65-byte (r||s||v) signature into (yParity, r, s). v=27/28 → yParity 0/1. */
export function splitSignature(sig: Hex): { yParity: number; r: bigint; s: bigint } {
  const hex = sig.slice(2)
  if (hex.length !== 130) throw new Error(`expected 65-byte sig, got ${hex.length / 2} bytes`)
  const r = BigInt(`0x${hex.slice(0, 64)}`)
  const s = BigInt(`0x${hex.slice(64, 128)}`)
  const v = parseInt(hex.slice(128, 130), 16)
  return { yParity: v >= 27 ? v - 27 : v, r, s }
}
